import cn from 'classnames';
import React, { InputHTMLAttributes, useEffect, useRef, useState } from 'react';

interface InputNumberProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string | number;
  onChange: (value: string | number) => void;
  isDisabled?: boolean;
}

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
    ({ isDisabled, value, onChange, className, ...rest }) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const spanRef = useRef<HTMLInputElement> (null);
        const [isFocused, setIsFocused] = useState(false);

        useEffect(() => {
            const handleMouseInput = () => {
                setIsFocused(true);
            };

            const handleKeyboardInput = () => {
                setIsFocused(false);
            };

            window.addEventListener('mousedown', handleMouseInput);
            window.addEventListener('keydown', handleKeyboardInput);

            return (): void => {
                window.removeEventListener('mousedown', handleMouseInput);
                window.removeEventListener('keydown', handleKeyboardInput);
            };
        }, []);

        useEffect(() => {
            // Make the input auto-resize based on its content
            if (spanRef.current && inputRef.current) {
                spanRef.current.textContent = String(value);
                const width = spanRef.current.offsetWidth + 16;
                const minWidth = window.innerWidth >= 1024 ? 40 : 36;
                inputRef.current.style.width = `${Math.max(width, minWidth)+4}px`;
            }
        }, [value]);

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            // Select all text when input is focused
            e.target.select();
            // Call the original onFocus if provided
            if (rest.onFocus) {
                rest.onFocus(e);
            }
        };

        return (
            <>
                <span
                    ref={spanRef}
                    className="tw-invisible tw-absolute tw-whitespace-pre"
                />
                <input
                    ref={inputRef}
                    id="input_number"
                    type="number"
                    disabled={isDisabled}
                    onFocus={handleFocus}
                    value={value}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            onChange('');
                            return;
                        }

                        const parsed = Number(val);
                        if (!isNaN(parsed)) {
                            onChange(parsed);
                        }
                    }}
                    className={cn(
                        'tw-h-10 tw-px-2 tw-py-2 tw-inline-flex tw-w-auto',
                        'tw-border-solid tw-border tw-border-primary tw-bg-fill-primary tw-rounded',
                        'tw-min-w-[36px] lg:tw-min-w-[40px] tw- tw-justify-center tw-items-center',
                        'tw-field-sizing-content tw-text-center tw-text-t-sm lg-font-dynamic',
                        '[appearance:textfield] [&::-webkit-inner-spin-button]:tw-appearance-none [&::-webkit-outer-spin-button]:tw-appearance-none',
                        !isDisabled && !isFocused && 'focus-visible:!tw-outline-none focus-visible:tw-border-solid focus-visible:tw-border-2 focus-visible:tw-border-primary-focus',
                        !isDisabled && isFocused && 'focus-visible:!tw-outline-none focus:tw-border-primary-active',
                        !isDisabled &&'tw-text-t-primary active:tw-border-primary-press hover:tw-border-primary-hover',
                        isDisabled && '!tw-text-t-inactive tw-bg-fill-secondary tw-border-solid tw-border tw-border-primary tw-rounded ',
                        className
                    )}
                    {...rest}
                />
            </>
        );
    }
);

InputNumber.displayName = 'InputNumber';

export { InputNumber };
